import { getApiError } from '../../utils/errorHelper';
import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Pencil, Trash2, Save, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { documentTypesApi } from '../../services/api';

const FILE_TYPE_OPTIONS = ['pdf', 'jpg', 'png', 'doc', 'docx', 'xls', 'xlsx'];

const emptyForm = {
  name: '',
  description: '',
  file_types: ['pdf'],
  is_required_default: true
};

// Separate DocumentTypeForm component to prevent re-render issues
const DocumentTypeForm = ({ initialData, onSave, onCancel, isNew, saving }) => {
  const [form, setForm] = useState(initialData || emptyForm);

  const handleFieldChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleFileType = useCallback((type) => {
    setForm(prev => {
      const newTypes = prev.file_types.includes(type)
        ? prev.file_types.filter(t => t !== type)
        : [...prev.file_types, type];
      return { ...prev, file_types: newTypes.length > 0 ? newTypes : ['pdf'] };
    });
  }, []);

  const handleSubmit = () => {
    onSave(form);
  };

  return (
    <div className="bg-slate-900/80 rounded-lg p-4 border border-indigo-500/30 space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-indigo-400" />
        <span className="text-white font-medium">{isNew ? 'Add Document Type' : 'Edit Document Type'}</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Name *</label>
          <input 
            type="text"
            placeholder="e.g., PAN Card, Certificate of Incorporation"
            value={form.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 focus:outline-none focus:border-indigo-500"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Description</label>
          <input 
            type="text"
            placeholder="Brief description"
            value={form.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 focus:outline-none focus:border-indigo-500"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-400 block mb-2">Allowed File Types</label>
        <div className="flex flex-wrap gap-2">
          {FILE_TYPE_OPTIONS.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => toggleFileType(type)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                form.file_types.includes(type)
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              .{type}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_required"
          checked={form.is_required_default}
          onChange={(e) => handleFieldChange('is_required_default', e.target.checked)}
          className="rounded bg-slate-700 border-slate-600"
        />
        <label htmlFor="is_required" className="text-sm text-slate-300">Required by default</label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="border-slate-600 text-slate-300">
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {isNew ? 'Create' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// Main DocumentTypesTab component
const DocumentTypesTab = () => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingDocType, setEditingDocType] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadDocumentTypes();
  }, []);

  const loadDocumentTypes = async () => {
    setLoading(true);
    try {
      const data = await documentTypesApi.getAllAdmin();
      setDocumentTypes(data || []);
    } catch (error) {
      toast.error('Failed to load document types');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingId(null);
    setEditingDocType(null);
    setShowForm(true);
  };

  const handleEdit = (docType) => {
    setEditingId(docType.id);
    setEditingDocType({
      name: docType.name || '',
      description: docType.description || '',
      file_types: docType.file_types || ['pdf'],
      is_required_default: docType.is_required_default !== false
    });
    setShowForm(true);
  };

  const handleSave = async (formData) => {
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingId) {
        await documentTypesApi.update(editingId, formData);
        toast.success('Document type updated');
      } else {
        await documentTypesApi.create(formData);
        toast.success('Document type created');
      }
      setShowForm(false);
      setEditingId(null);
      setEditingDocType(null);
      loadDocumentTypes();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to save document type'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setEditingDocType(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document type?')) return;
    
    try {
      await documentTypesApi.delete(id);
      toast.success('Document type deleted');
      loadDocumentTypes();
    } catch (error) {
      toast.error('Failed to delete document type');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Document Types</h2>
          <p className="text-slate-400 text-sm mt-1">Manage document types that can be required for products</p>
        </div>
        {!showForm && (
          <Button onClick={handleCreateNew} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" /> Add Document Type
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <DocumentTypeForm
          initialData={editingDocType}
          onSave={handleSave}
          onCancel={handleCancel}
          isNew={!editingId}
          saving={saving}
        />
      )}

      {/* Document Types List */}
      {!showForm && (
        loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
          </div>
        ) : documentTypes.length > 0 ? (
          <div className="bg-slate-900/40 border border-slate-700/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="document-types-table">
              <thead className="bg-slate-800/60 border-b border-slate-700/50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium">File Types</th>
                  <th className="px-4 py-3 font-medium">Required</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {documentTypes.map(docType => (
                  <tr
                    key={docType.id}
                    className={`hover:bg-slate-800/40 transition-colors ${docType.is_active ? '' : 'opacity-60'}`}
                    data-testid={`document-type-row-${docType.id}`}
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <span className="text-white font-medium">{docType.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-400 max-w-md">
                      {docType.description || <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        {(docType.file_types || ['pdf']).map(type => (
                          <span key={type} className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                            .{type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {docType.is_required_default ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                          <CheckCircle className="w-3 h-3" /> Required
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                          <AlertCircle className="w-3 h-3" /> Optional
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {docType.is_active ? (
                        <span className="text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      ) : (
                        <span className="text-xs bg-red-500/15 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleEdit(docType)}
                          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                          data-testid={`document-type-edit-btn-${docType.id}`}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(docType.id)}
                          className="p-1.5 rounded hover:bg-red-900/50 text-red-400 transition-colors"
                          data-testid={`document-type-delete-btn-${docType.id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-slate-700/50 rounded-xl">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No document types created yet</p>
            <p className="text-slate-500 text-sm mt-1">Click &quot;Add Document Type&quot; to create one</p>
          </div>
        )
      )}
    </div>
  );
};

export default DocumentTypesTab;
